<template>
  <span
    class="search-switch"
    v-if="search_display_type === 'show'"
  >
    <a-button
      v-if="!isShowForm"
      type="link"
      size="small"
      @click="isShowForm = true"
    >
      <span class="expand-btn">{{ $t('展开') }}</span>
    </a-button>
    <a-button
      v-else
      type="link"
      size="small"
      @click="isShowForm = false"
    >
      {{ $t('隐藏') }}
    </a-button>
  </span>
  <!-- 多分组模式：搜索摘要 -->
  <template v-if="!isShowForm && search_display_type === 'hide_and_show_summary'">
    <template
      v-for="(group, index) in searchGroups"
      :key="group.id"
    >
      <span
        class="group-or-separator"
        v-if="index > 0"
      >
        OR
      </span>
      <search-summary
        :search-conditions="group.conditions"
        :search-config="searchConfig"
        :field-search-config="fieldSearchConfig"
        :event-bus="eventBus"
        :searchType="searchType"
        :showRefresh="index === searchGroups.length - 1"
        @update:searchConditions="conditions => handleGroupSummaryUpdate(group.id, conditions)"
        @change="handleGroupSearchChange"
      />
    </template>
  </template>
  <!-- 多分组模式：搜索表单 -->
  <div
    v-if="isShowForm"
    class="search-groups-container"
  >
    <div
      v-for="(group, index) in searchGroups"
      :key="group.id"
      class="search-group-item"
    >
      <div class="search-group-header">
        <span class="search-group-header-title">{{ $t('分组') + ' ' + (index + 1) }}</span>
        <div class="search-group-actions">
          <a-button
            type="text"
            size="small"
            @click="searchConfigVisibleMap[group.id] = true"
            v-if="config?.sys_btn?.search_field_config !== false"
          >
            <a-tooltip :title="$t('设置搜索字段')">
              <SettingOutlined />
            </a-tooltip>
          </a-button>
          <a-button
            type="text"
            size="small"
            danger
            @click="handleRemoveGroup(group.id)"
            v-if="searchGroups.length > 1"
          >
            <a-tooltip :title="$t('删除分组')">
              <DeleteOutlined />
            </a-tooltip>
          </a-button>
        </div>
      </div>
      <div
        class="search-form-wraper"
        :style="{
          '--column-count': searchColumnCount || 0,
        }"
      >
        <SearchForm
          :searchConfig="searchConfig"
          :searchConditions="group.conditions"
          :viewSearchConfig="group.viewSearchConfig"
          :searchType="searchType"
          :searchColumnCount="searchColumnCount"
          :autoRefreshOnSearchChange="autoRefreshOnSearchChange"
          :eventBus="eventBus"
          :disabledShowSearchGroupName="disabledShowSearchGroupName"
          @update:searchConditions="conditions => handleGroupConditionsUpdate(group.id, conditions)"
          @change="handleGroupSearchChange"
        />
      </div>
      <search-config-modal
        v-model:open="searchConfigVisibleMap[group.id]"
        :search-config="searchConfig"
        :view-search-config="group.viewSearchConfig"
        @change="config => handleGroupViewSearchConfigChange(group.id, config)"
      />
    </div>
    <a-button
      class="add-group-btn"
      size="small"
      type="dashed"
      @click="handleAddGroup"
    >
      <PlusOutlined />
      {{ $t('添加分组') }}
    </a-button>
  </div>
  <div
    class="bottom-content"
    v-if="isShowForm && !autoRefreshOnSearchChange"
  >
    <a-button
      size="small"
      type="primary"
      @click="handleRefresh"
    >
      查询
    </a-button>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, PropType, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import searchSummary from './searchSummary.vue';
import SearchConfigModal from './tableConfig/searchConfig.vue';
import SearchForm from './SearchForm.vue';
import { SearchConfig, SearchGroupItem, SearchType, ViewSearchItem } from './typing';
import { useTableProState } from './utils';
import { genID } from 'liyu-pc-base/utils/function.js';

defineOptions({
  name: 'SearchFormGroup',
});

const props = defineProps({
  searchConfig: {
    type: Array as PropType<SearchConfig[]>,
    required: true,
  },
  /** 表头自定义搜索字段配置（query_field_search_config_url 查询结果），用于搜索摘要展示未在 search_config 中配置的字段 */
  fieldSearchConfig: {
    type: Array as PropType<SearchConfig[]>,
    default: () => [],
  },
  searchGroups: {
    type: Array as PropType<SearchGroupItem[]>,
    required: true,
  },
  eventBus: {
    type: Object as any,
    required: false,
  },
  searchType: {
    type: String as () => SearchType,
    default: 'advanced',
  },
  searchDisplayType: {
    type: String,
    default: 'hide',
  },
  searchColumnCount: {
    type: Number,
    default: 0,
  },
  autoRefreshOnSearchChange: {
    type: Boolean,
    default: false,
  },
  disabledShowSearchGroupName: {
    type: Boolean,
    default: false, // 是否显示搜索分组名称，默认不显示
  },
});

const emit = defineEmits(['update:searchGroups', 'change']);

const { t } = useI18n();
const isShowForm = ref(false);
const searchConfigVisibleMap = ref<Record<string, boolean>>({});
const { config } = useTableProState();

const handleRefresh = () => {
  props.eventBus.emit('refresh');
};

const handleAddGroup = () => {
  const firstGroupViewSearchConfig = props.searchGroups[0]?.viewSearchConfig || {};
  const newGroupViewSearchConfig = Object.keys(firstGroupViewSearchConfig).reduce((acc, key) => {
    acc[key] = { ...firstGroupViewSearchConfig[key] };
    return acc;
  }, {} as Record<string, ViewSearchItem>);
  const newGroups = [
    ...props.searchGroups,
    {
      id: genID(),
      conditions: [],
      viewSearchConfig: newGroupViewSearchConfig,
    },
  ];
  emit('update:searchGroups', newGroups);
  emit('change');
};

const handleRemoveGroup = (groupId: string) => {
  const newGroups = props.searchGroups.filter(g => g.id !== groupId);
  emit('update:searchGroups', newGroups);
  emit('change');
  if (props.autoRefreshOnSearchChange) {
    handleRefresh();
  }
};

const handleGroupConditionsUpdate = (groupId: string, conditions: any[]) => {
  const newGroups = props.searchGroups.map(g => (g.id === groupId ? { ...g, conditions } : g));
  emit('update:searchGroups', newGroups);
};

const handleGroupViewSearchConfigChange = (
  groupId: string,
  viewSearchConfig: Record<string, ViewSearchItem>,
) => {
  const newGroups = props.searchGroups.map(g =>
    g.id === groupId ? { ...g, viewSearchConfig } : g,
  );
  emit('update:searchGroups', newGroups);
  emit('change');
};

const handleGroupSearchChange = () => {
  emit('change');
};

const handleGroupSummaryUpdate = (groupId: string, conditions: any[]) => {
  const newGroups = props.searchGroups.map(g => (g.id === groupId ? { ...g, conditions } : g));
  emit('update:searchGroups', newGroups);
  handleRefresh();
};

const search_display_type = computed(() => {
  return props.searchDisplayType || 'hide';
});

onMounted(() => {
  if (search_display_type.value === 'show_no_hide') {
    isShowForm.value = true;
  }
});
</script>

<style lang="less" scoped>
.search-switch {
  display: inline-block;
  line-height: 32px;
  height: 32px;
}
.search-groups-container {
  max-height: 300px;
  overflow-y: auto;
  padding: 4px 0;
}
.search-group-item {
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  padding: 8px;
  margin-bottom: 8px;
  background: #fafafa;
}
.search-group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  padding-bottom: 4px;
  border-bottom: 1px dashed #e0e0e0;
}
.search-group-header-title {
  font-weight: bold;
  color: var(--primary-color, #1890ff);
  font-size: 13px;
}
.search-group-actions {
  display: flex;
  gap: 4px;
}
.search-form-wraper {
  padding-left: 2px;
  padding-right: 2px;
}
.add-group-btn {
  margin: 4px 0 8px 8px;
}
.bottom-content {
  display: flex;
  justify-content: flex-end;
  padding: 8px 26px 8px 8px;
}
.group-or-separator {
  display: inline-block;
  padding: 0 8px;
  color: #999;
  font-size: 12px;
  font-weight: bold;
  line-height: 32px;
}
</style>
